/**
 * 🚀 useMutation Hook - POST/PUT/DELETE 요청을 위한 React Hook
 *
 * 이 훅은 SWR을 사용한 GET 요청(useRequest)과 달리,
 * 데이터 생성/수정/삭제를 위한 mutation 작업에 특화되어 있습니다.
 *
 * @features
 * - ✅ POST, PUT, DELETE, PATCH 메소드 지원
 * - ✅ TypeScript 완전 지원
 * - ✅ 로딩 상태, 에러 처리 자동 관리
 * - ✅ Axios 기반 요청 처리
 * - ✅ SWR v1 호환성 (useSWRMutation 대신 useState 사용)
 *
 * @basic-usage
 * ```typescript
 * import { usePostRequest } from '@/hooks';
 *
 * function MyComponent() {
 *   const { trigger, isLoading, data, error } = usePostRequest('/api/posts');
 *
 *   const handleSubmit = async (formData) => {
 *     try {
 *       const result = await trigger(formData);
 *       console.log('성공:', result);
 *     } catch (err) {
 *       console.error('에러:', err);
 *     }
 *   };
 *
 *   if (isLoading) return <div>저장 중...</div>;
 *
 *   return <button onClick={handleSubmit}>게시글 생성</button>;
 * }
 * ```
 *
 * @available-methods
 * - useMutation: 범용 HTTP 메소드 지원
 * - usePostRequest: POST 전용 (권장)
 * - usePutRequest: PUT 전용
 * - useDeleteRequest: DELETE 전용
 *
 * @return-object
 * ```typescript
 * {
 *   data: TData,           // 응답 데이터
 *   error: AxiosError,     // 에러 객체
 *   isLoading: boolean,    // 로딩 상태
 *   trigger: Function,     // 요청 실행 함수
 *   reset: Function        // 상태 초기화 함수
 * }
 * ```
 *
 * @tips
 * - trigger 함수는 Promise를 반환하므로 async/await 사용 권장
 * - 에러 처리는 try/catch 또는 error 상태로 처리 가능
 * - 여러 번 호출 시 이전 요청은 자동 취소되지 않음
 *
 */

import { useCallback, useState } from 'react';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { coreApi } from '@/api';

interface MutationState<Data = unknown, Error = unknown> {
  data?: Data;
  error?: AxiosError<Error>;
  isLoading: boolean;
}

export type HttpMethod = 'POST' | 'PUT' | 'DELETE' | 'PATCH';

interface MutationOptions extends AxiosRequestConfig {
  skipGlobalErrorToast?: boolean;
}

export function useMutation<Data = unknown, Error = unknown>(
  route: string,
  method: HttpMethod = 'POST',
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  const [state, setState] = useState<MutationState<Data, Error>>({
    isLoading: false
  });

  const trigger = async (data?: any): Promise<Data | undefined> => {
    setState({ isLoading: true });

    try {
      const config = { ...axiosConfig };
      if (data) {
        config.data = data;
      }

      let response: AxiosResponse<Data>;

      switch (method) {
        case 'POST':
          response = await axiosInstance.post<Data>(route, config.data, config);
          break;
        case 'PUT':
          response = await axiosInstance.put<Data>(route, config.data, config);
          break;
        case 'DELETE':
          response = await axiosInstance.delete<Data>(route, config);
          break;
        case 'PATCH':
          response = await axiosInstance.patch<Data>(route, config.data, config);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      setState({
        data: response.data,
        isLoading: false
      });

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<Error>;
      setState({
        error: axiosError,
        isLoading: false
      });
      throw axiosError;
    }
  };

  const reset = () => {
    setState({ isLoading: false });
  };

  return {
    ...state,
    trigger,
    reset
  };
}

// 편의를 위한 별칭 함수들
export function usePostRequest<Data = unknown, Error = unknown>(
  route: string,
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  return useMutation<Data, Error>(route, 'POST', axiosConfig, axiosInstance);
}

export function usePutRequest<Data = unknown, Error = unknown>(
  route: string,
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  return useMutation<Data, Error>(route, 'PUT', axiosConfig, axiosInstance);
}

export function useDeleteRequest<Data = unknown, Error = unknown>(
  route: string,
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  return useMutation<Data, Error>(route, 'DELETE', axiosConfig, axiosInstance);
}

export function useDynamicMutation<Data = unknown, Error = unknown>(
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  const [state, setState] = useState<MutationState<Data, Error>>({
    isLoading: false
  });

  const mutateByPath = useCallback(
    async (route: string, method: HttpMethod = 'POST', data?: any): Promise<Data | undefined> => {
      setState({ isLoading: true });

      try {
        const config = {
          ...axiosConfig,
          skipGlobalErrorToast: true // 전역 에러 토스트 비활성화
        };

        let response: AxiosResponse<Data>;

        switch (method) {
          case 'POST':
            response = await axiosInstance.post<Data>(route, data, config);
            break;
          case 'PUT':
            response = await axiosInstance.put<Data>(route, data, config);
            break;
          case 'DELETE':
            response = await axiosInstance.delete<Data>(route, config);
            break;
          case 'PATCH':
            response = await axiosInstance.patch<Data>(route, data, config);
            break;
          default:
            throw new Error(`Unsupported HTTP method: ${method}`);
        }

        setState({
          data: response.data,
          isLoading: false
        });

        return response.data;
      } catch (error) {
        const axiosError = error as AxiosError<Error>;
        setState({
          error: axiosError,
          isLoading: false
        });
        throw axiosError;
      }
    },
    [axiosConfig, axiosInstance]
  );

  const reset = () => {
    setState({ isLoading: false });
  };

  return {
    ...state,
    mutateByPath,
    reset
  };
}

// 편의를 위한 특화 함수들
export function useDynamicDeleteRequest<Data = unknown, Error = unknown>(
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  const { mutateByPath, ...rest } = useDynamicMutation<Data, Error>(axiosConfig, axiosInstance);

  const deleteByPath = useCallback(
    async (route: string): Promise<Data | undefined> => {
      return mutateByPath(route, 'DELETE');
    },
    [mutateByPath]
  );

  return {
    ...rest,
    deleteByPath
  };
}

export function useDynamicPostRequest<Data = unknown, Error = unknown>(
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  const { mutateByPath, ...rest } = useDynamicMutation<Data, Error>(axiosConfig, axiosInstance);

  const postByPath = useCallback(
    async (route: string, data?: any): Promise<Data | undefined> => {
      return mutateByPath(route, 'POST', data);
    },
    [mutateByPath]
  );

  return {
    ...rest,
    postByPath
  };
}

export function useDynamicPutRequest<Data = unknown, Error = unknown>(
  axiosConfig?: MutationOptions,
  axiosInstance = coreApi
) {
  const { mutateByPath, ...rest } = useDynamicMutation<Data, Error>(axiosConfig, axiosInstance);

  const putByPath = useCallback(
    async (route: string, data?: any): Promise<Data | undefined> => {
      return mutateByPath(route, 'PUT', data);
    },
    [mutateByPath]
  );

  return {
    ...rest,
    putByPath
  };
}
